import { useEffect, useState } from 'react';

export type ThemeClassName = 'str-chat__theme-light' | 'str-chat__theme-dark';
export type Theme = 'light' | 'dark';

export const SUPPORTED_THEMES: Record<Theme, ThemeClassName> = {
  light: 'str-chat__theme-light',
  dark: 'str-chat__theme-dark',
} as const;

export const DEFAULT_THEME: Theme = 'light';

/**
 * Internal, handles the communication between Stream's website related to UI theme toggling.
 *
 * @param targetOrigin - the target origin (typically, the https://getstream.io/ domain).
 * @param themeValue - the initial theme value as a string.
 */
export const useTheme = (targetOrigin: string, themeValue: string) => {
  // Ensure the initial theme is valid
  const initialTheme: Theme = themeValue in SUPPORTED_THEMES ? (themeValue as Theme) : DEFAULT_THEME;
  const [theme, setTheme] = useState<Theme>(initialTheme);

  useEffect(() => {
    const handleThemeChange = ({ data, origin }: MessageEvent) => {
      // Handle events only from trusted origin and ensure data is a valid theme
      if (origin === targetOrigin && typeof data === 'string' && data in SUPPORTED_THEMES) {
        setTheme(data as Theme);
      }
    };

    window.addEventListener('message', handleThemeChange);
    return () => {
      window.removeEventListener('message', handleThemeChange);
    };
  }, [targetOrigin]);

  return theme;
};
